import { CheckCircle, Lightbulb, HelpCircle, Compass } from "lucide-react";

/**
 * Renders an LLM completion as typed claim blocks — Evidence / Inference /
 * Assumption / Recommendation / Narrative — each with a distinct visual
 * band so an auditor can see at a glance which parts of the answer are
 * grounded fact vs. derived reasoning vs. stated hypothesis vs. proposed
 * action (Definition of Done §8).
 *
 * If `blocks` is empty or contains only a single `narrative` block (the
 * LLM ignored the tagging instructions entirely), we fall back to
 * rendering `fallbackText` as plain prose so the reader is never left
 * with a blank card.
 */
const CLASS_META = {
  evidence: {
    label: "Evidence",
    band: "border-l-4 border-emerald-500 bg-emerald-50/50",
    pill: "bg-emerald-100 text-emerald-800",
    Icon: CheckCircle,
  },
  inference: {
    label: "Inference",
    band: "border-l-4 border-sky-500 bg-sky-50/50",
    pill: "bg-sky-100 text-sky-800",
    Icon: Lightbulb,
  },
  assumption: {
    label: "Assumption",
    band: "border-l-4 border-amber-500 bg-amber-50/50",
    pill: "bg-amber-100 text-amber-800",
    Icon: HelpCircle,
  },
  recommendation: {
    label: "Recommendation",
    band: "border-l-4 border-ek-purple bg-ek-purple/5",
    pill: "bg-ek-purple/15 text-ek-purple",
    Icon: Compass,
  },
  narrative: {
    label: null,
    band: "",
    pill: "",
    Icon: null,
  },
};

export const TaggedBlocks = ({ blocks, fallbackText, dense = false, testIdPrefix = "tagged" }) => {
  const isMeaningful =
    Array.isArray(blocks) &&
    blocks.length > 0 &&
    blocks.some((b) => b?.class && b.class !== "narrative");

  if (!isMeaningful) {
    return (
      <p
        className="whitespace-pre-line text-sm leading-relaxed text-ek-ink/80"
        data-testid={`${testIdPrefix}-plain`}
      >
        {fallbackText || (blocks?.[0]?.text ?? "")}
      </p>
    );
  }

  return (
    <div className={dense ? "space-y-2" : "space-y-3"} data-testid={`${testIdPrefix}-blocks`}>
      {blocks.map((b, i) => {
        const meta = CLASS_META[b.class] || CLASS_META.narrative;
        if (b.class === "narrative") {
          return (
            <p
              key={`n-${i}`}
              className="whitespace-pre-line text-sm leading-relaxed text-ek-ink/75"
              data-testid={`${testIdPrefix}-block-narrative`}
            >
              {b.text}
            </p>
          );
        }
        const { label, band, pill, Icon } = meta;
        return (
          <div
            key={`t-${i}`}
            className={`rounded-md ${band} px-3 py-2.5`}
            data-testid={`${testIdPrefix}-block-${b.class}`}
          >
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${pill}`}
            >
              {Icon && <Icon size={11} />} {label}
            </span>
            <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-ek-ink/85">
              {b.text}
            </p>
          </div>
        );
      })}
    </div>
  );
};
